/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1792540800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Oct 21 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "databricks-destination",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Databricks",
          "depth": 0,
          "id": "connect-to-databricks"
        }, {
          "value": "Configuring a service principal with OAuth",
          "depth": 1,
          "id": "configuring-a-service-principal-with-oauth"
        }, {
          "value": "Alternative: Configuring a Personal Access Token (PAT)",
          "depth": 1,
          "id": "alternative-configuring-a-personal-access-token-pat"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Column types",
          "depth": 1,
          "id": "column-types"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Batch size",
          "depth": 1,
          "id": "batch-size"
        }, {
          "value": "Delete behavior",
          "depth": 1,
          "id": "delete-behavior"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      p: "p",
      a: "a",
      strong: "strong",
      h3: "h3",
      ol: "ol",
      li: "li",
      code: "code",
      em: "em",
      ul: "ul",
      img: "img"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Any data set"
            }), _jsx(_components.td, {
              children: "Sync data from any source to a Databricks table"
            }), _jsx(_components.td, {
              children: "Upsert, Insert, Mirror"
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-databricks",
        children: "Connect to Databricks"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Databricks"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "Databricks"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch allows you to authenticate with Databricks using one of two methods:\nEither ", _jsx(_components.a, {
          href: "https://docs.databricks.com/en/dev-tools/auth/oauth-u2m.html",
          children: "OAuth"
        }), " or\na ", _jsx(_components.a, {
          href: "https://docs.databricks.com/en/dev-tools/auth/pat.html",
          children: "Personal Access Token (PAT)"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "We recommend using OAuth for configuring new connections."
      }), "\n", _jsx(_components.h3, {
        id: "configuring-a-service-principal-with-oauth",
        children: "Configuring a service principal with OAuth"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Create a ", _jsx(_components.a, {
              href: "https://docs.databricks.com/en/admin/users-groups/service-principals.html",
              children: "service principal"
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["In your Databricks workspace, go to the user dropdown at the top right and select ", _jsx(_components.strong, {
              children: "Settings"
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["In the ", _jsx(_components.strong, {
              children: "Identity and access"
            }), " page, find ", _jsx(_components.strong, {
              children: "Service Principals"
            }), " and click ", _jsx(_components.strong, {
              children: "Manage"
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Choose ", _jsx(_components.strong, {
              children: "Add new"
            }), ", give your service principal a name, and click ", _jsx(_components.strong, {
              children: "Add"
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Take note of the Service Principal name as well as the ", _jsx(_components.strong, {
              children: "Application id"
            }), " listed on the Service Principal\nConfigurations page. This is a UUID (for example, ", _jsx(_components.code, {
              children: "63fc7e90-a8a2-4639-afd8-36ef6bb67cfa"
            }), "). You may\nneed this later to complete setup."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Create an OAuth secret pair."
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "On the page for the Service Principal created above, to go the Secrets tab."
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Click ", _jsx(_components.strong, {
              children: "Generate secret"
            }), ". A dialog will pop up with a ", _jsx(_components.strong, {
              children: "Secret"
            }), " and a ", _jsx(_components.strong, {
              children: "Client ID"
            }), ".\nTake careful note of these values as it is only visible now; you can't look it up later."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Grant the necessary catalog permissions to your new Service Principal. The above steps are sufficient\nto connect and authenticate, but the Service Principal may not be ", _jsx(_components.em, {
              children: "authorized"
            }), " to execute\nqueries against your Databricks catalog. Depending on your Databricks workspace configuration,\nit may already be configured with the necessary permissions; but if the connection test fails,\nyou may need to follow these steps."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["In the Databricks sidebar on the left, click ", _jsx(_components.strong, {
              children: "Catalog"
            }), " and locate your schema in the tree view."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Select the schema and open the ", _jsx(_components.strong, {
              children: "Permissions"
            }), " page. Click the ", _jsx(_components.strong, {
              children: "Grant"
            }), " button."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["In the ", _jsx(_components.strong, {
              children: "Principals"
            }), " input box, type the ", _jsx(_components.strong, {
              children: "name"
            }), " of your new Service Principal."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["select the ", _jsx(_components.strong, {
          children: "Data Editor"
        }), " preset or manually grant the following permissions:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "USE SCHEMA"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "EXECUTE"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "CREATE VOLUME"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "READ VOLUME"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "SELECT"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "MODIFY"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "CREATE TABLE"
          })
        }), "\n", _jsxs(_components.li, {
          children: ["Make sure to enable the checkbox at the bottom to \"Also grant ", _jsx(_components.code, {
            children: "USE CATALOG"
          }), "\""]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["These permissions apply to a schema you've already created. If you use Databricks as an ", _jsx(_components.a, {
          href: "/events/warehouses/warehouse#databricks",
          children: "event storage destination"
        }), " and want Hightouch to create the schema for you, also grant ", _jsx(_components.code, {
          children: "CREATE SCHEMA"
        }), " on the catalog."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["You may also need to configure ", _jsx(_components.em, {
              children: "warehouse"
            }), " permissions. As with the previous step\nconcerning ", _jsx(_components.em, {
              children: "catalog"
            }), " permissions, your Databricks workspace configuration may or may not\nautomatically grant the Service Principal permission to use the compute resources.\nYou can read more about it in the ", _jsx(_components.a, {
              href: "https://docs.databricks.com/en/compute/clusters-manage.html#compute-permissions",
              children: "Databricks docs"
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["In the Databricks sidebar, click ", _jsx(_components.strong, {
              children: "Compute"
            }), " and find your data warehouse. It may\nbe located on the ", _jsx(_components.strong, {
              children: "SQL warehouses"
            }), " page."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["On the far right column of the row corresponding to the desired warehouse, click\nthe icon for the kebab menu ", _jsx(_components.strong, {
              children: "⋮"
            }), ", then ", _jsx(_components.strong, {
              children: "Permissions"
            }), "."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["If you do not see the ", _jsx(_components.strong, {
            children: "Permissions"
          }), " option, you may lack the privileges\nnecessary to configure permissions—even if you are a Databricks workspace\nadmin. If this happens, please contact your Databricks account admin."]
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Enter the name of your Service Principal and ensure that the setting in the\ndropdown is set to ", _jsx(_components.strong, {
            children: "Can use"
          }), ". Click ", _jsx(_components.strong, {
            children: "Add"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "alternative-configuring-a-personal-access-token-pat",
        children: "Alternative: Configuring a Personal Access Token (PAT)"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In your Databricks Account console, go the ", _jsxs(_components.a, {
            href: "https://accounts.cloud.databricks.com/workspaces",
            children: [_jsx(_components.strong, {
              children: "Workspaces"
            }), " page"]
          }), ".\nSelect the relevant workspace and then click ", _jsx(_components.strong, {
            children: "Open workspace"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "sources/source-databricks-workspace.png",
          alt: "Databricks workspace settings"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "2",
        children: ["\n", _jsxs(_components.li, {
          children: ["In your workspace, go the ", _jsx(_components.strong, {
            children: "Compute"
          }), " page and click the relevant cluster. This brings you to its ", _jsx(_components.strong, {
            children: "Configuration"
          }), " tab."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "sources/source-databricks-settings.png",
          alt: "Databricks cluster settings"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsxs(_components.li, {
          children: ["At the bottom of the page, expand the ", _jsx(_components.strong, {
            children: "Advanced Options"
          }), " toggle and open the ", _jsx(_components.strong, {
            children: "JDBC/ODBC"
          }), " tab."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "sources/source-databricks-advanced.png",
          alt: "Databricks Advanced Options"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "4",
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["This tab displays your cluster's ", _jsx(_components.strong, {
              children: "Server Hostname"
            }), ", ", _jsx(_components.strong, {
              children: "Port"
            }), ", and ", _jsx(_components.strong, {
              children: "HTTP Path"
            }), ", which you need to connect to Hightouch.\nKeep the tab open, or save these to a secure location."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Create a ", _jsx(_components.strong, {
              children: "Personal access token"
            }), " by following ", _jsx(_components.a, {
              href: "https://docs.databricks.com/dev-tools/auth.html#pat",
              children: "the Databricks documentation"
            }), "."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Once you've saved these Databricks credentials, you're ready to set up the connection in Hightouch."
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Databricks destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Databricks destination you want to sync to."]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports ", _jsx(_components.strong, {
          children: "Upsert"
        }), " mode using the ", _jsx(_components.code, {
          children: "MERGE INTO"
        }), " statement, with the option to ", _jsx(_components.a, {
          href: "#delete-behavior",
          children: "delete removed rows"
        }), ", ", _jsx(_components.strong, {
          children: "Insert"
        }), " mode using the ", _jsx(_components.code, {
          children: "INSERT INTO"
        }), " statement, and ", _jsx(_components.strong, {
          children: "Mirror"
        }), " mode which will ", _jsx(_components.code, {
          children: "TRUNCATE"
        }), " the table and then use the ", _jsx(_components.code, {
          children: "INSERT INTO"
        }), " statement to insert all rows from the model."]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "Mirror"
          }), " mode will remove any existing data in the table before inserting all rows from the model"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["To match rows from your model to rows in Databricks, Hightouch requires you to select a unique identifier in the table you are syncing to.\nThe model column you select must be the same as the ", _jsx(_components.a, {
          href: "/models/creating-models#unique-primary-key-requirement",
          children: "primary key"
        }), " column you selected in your model."]
      }), "\n", _jsxs(_components.p, {
        children: ["The Databricks column you select must be set as a ", _jsx(_components.code, {
          children: "PRIMARY KEY"
        }), " column within Databricks.\nYou can see columns that fit this criteria as available options in records matching section.\nIf there are no fields in the dropdown, you must add a unique type column to your Databricks table. Then, click the refresh icon to access the newly created column."]
      }), "\n", _jsx(_components.h3, {
        id: "column-types",
        children: "Column types"
      }), "\n", _jsxs(_components.p, {
        children: ["If you see type errors, it may be because your model is producing the wrong format. If so, use ", _jsx(_components.a, {
          href: "/models/data-types-casting",
          children: "typecasting"
        }), " or ", _jsx(_components.a, {
          href: "https://hightouch.com/docs/syncs/mapping-data#liquid-template-format",
          children: "liquid templating"
        }), " to resolve the issue."]
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your model to the columns in your Databricks table."
      }), "\n", _jsx(_components.h3, {
        id: "batch-size",
        children: "Batch size"
      }), "\n", _jsx(_components.p, {
        children: "You can tune the number of rows Hightouch upserts or inserts per query based on your needs and database threshold. The default is 50,000 rows per batch."
      }), "\n", _jsx(_components.p, {
        children: "If you want to increase the sync's speed, you can increase the batch size. Keep in mind that in upsert mode, Databricks fails the entire batch of rows if it detects any erroneous row. If you suspect that you will have many bad rows, don't use a high batch size. To avoid locks, ensure you account for your database's capacity when increasing the batch size."
      }), "\n", _jsx(_components.h3, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "/syncs/overview#delete-behavior",
          children: "delete behavior"
        }), " you select dictates what to do when a row no longer appears in your model's query results. You have the following options:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the row in Databricks"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete row"
              })
            }), _jsx(_components.td, {
              children: "Remove the row from Databricks entirely"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
